import React, { useState, useCallback } from "react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Slider,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import VisibilityIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffRounded";
import CameraAltRoundedIcon from "@mui/icons-material/CameraAltRounded";
import CropIcon from "@mui/icons-material/CropRounded";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";
import {
  EmailAuthProvider,
  getAuth,
  reauthenticateWithCredential,
  updatePassword,
  verifyBeforeUpdateEmail,
} from "firebase/auth";
import Cropper from "react-easy-crop";
import {
  getCroppedImg,
  createThumbnail,
  blobToBase64,
} from "helpers/imageUtils";
import { getAvatarColor } from "helpers/avatarHelpers";

const getTodayDateValue = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Password strength evaluation
const evaluatePasswordStrength = (password) => {
  if (!password) return { strength: 0, label: "", color: "grey" };

  let strength = 0;
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };

  // Length is worth 2 points, others 1 each
  if (checks.length) strength += 2;
  if (checks.uppercase) strength += 1;
  if (checks.lowercase) strength += 1;
  if (checks.number) strength += 1;
  if (checks.special) strength += 1;

  // Additional length bonuses
  if (password.length >= 12) strength += 1;
  if (password.length >= 16) strength += 1;

  // Map strength to label and color
  if (strength <= 2) {
    return { strength: 25, label: "Weak", color: "error", checks };
  } else if (strength <= 4) {
    return { strength: 50, label: "Fair", color: "warning", checks };
  } else if (strength <= 6) {
    return { strength: 75, label: "Good", color: "info", checks };
  } else {
    return { strength: 100, label: "Strong", color: "success", checks };
  }
};

const SettingsProfileEdit = () => {
  usePageTitle("Edit Profile");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { currentUser, setCurrentUser } = useAuth();
  const { socket } = useWebSocket();
  const { overlayData, setOverlayName, showSnackbar, handleErrorDialog } =
    useStateContext();

  const [name, setName] = useState(
    overlayData?.name ?? currentUser?.Name ?? "",
  );
  const [initialName] = useState(overlayData?.name ?? currentUser?.Name ?? "");
  const [email, setEmail] = useState(
    overlayData?.email ?? currentUser?.email ?? "",
  );
  const [initialEmail] = useState(
    overlayData?.email ?? currentUser?.email ?? "",
  );
  const [hourlyRate, setHourlyRate] = useState(() => {
    const source = overlayData?.hourlyRate ?? currentUser?.HourlyRate ?? currentUser?.hourlyRate ?? 0;
    const parsed = Number(source);
    return Number.isFinite(parsed) ? parsed.toFixed(2) : "0.00";
  });
  const [initialHourlyRate] = useState(() => {
    const source = overlayData?.hourlyRate ?? currentUser?.HourlyRate ?? currentUser?.hourlyRate ?? 0;
    const parsed = Number(source);
    return Number.isFinite(parsed) ? parsed.toFixed(2) : "0.00";
  });
  const [hourlyRateEffectiveDate, setHourlyRateEffectiveDate] = useState(
    getTodayDateValue(),
  );
  const [profileImage, setProfileImage] = useState(overlayData?.image ?? null);
  const [profileImagePreview, setProfileImagePreview] = useState(
    overlayData?.image ?? null,
  );
  const [imageToCrop, setImageToCrop] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const passwordStrength = evaluatePasswordStrength(newPassword);
  const passwordsDoNotMatch =
    newPassword && confirmPassword && newPassword !== confirmPassword;

  const closeOverlay = () => setOverlayName("");

  const memberId = overlayData?.memberId || currentUser?.id;

  const getFriendlyAuthError = (err) => {
    if (!err) return "Error updating password";
    const code = err.code || "";
    if (code === "auth/invalid-credential" || code === "auth/wrong-password") {
      return "Current password is incorrect or the session expired. Please sign in again.";
    }
    if (code === "auth/weak-password") {
      return "New password is too weak. Use at least 6 characters.";
    }
    return err.message || "Error updating password";
  };

  const isValidEmail = (value) => {
    const normalized = String(value || "").trim();
    if (!normalized) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showSnackbar("Image must be less than 5MB", theme, "error");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImageToCrop(reader.result);
        setShowCropDialog(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropSave = async () => {
    try {
      const croppedBlob = await getCroppedImg(imageToCrop, croppedAreaPixels);
      const thumbnailBlob = await createThumbnail(croppedBlob);

      // Convert to base64 strings
      const fullBase64 = await blobToBase64(croppedBlob);
      const thumbnailBase64 = await blobToBase64(thumbnailBlob);

      setProfileImage({ full: fullBase64, thumbnail: thumbnailBase64 });
      setProfileImagePreview(fullBase64); // Use base64 for preview
      setShowCropDialog(false);
      setImageToCrop(null);
    } catch (error) {
      console.error("Error cropping image:", error);
      showSnackbar("Error processing image", theme, "error");
    }
  };

  const handleCropCancel = () => {
    setShowCropDialog(false);
    setImageToCrop(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showSnackbar("Name is required", theme, "error");
      return;
    }

    const normalizedEmail = email.trim();
    const normalizedHourlyRate = String(hourlyRate ?? "").trim();
    const parsedHourlyRate = Number.parseFloat(normalizedHourlyRate || "0");

    if (!Number.isFinite(parsedHourlyRate) || parsedHourlyRate < 0) {
      showSnackbar("Hourly rate must be a valid non-negative number", theme, "error");
      return;
    }

    const normalizedHourlyRateValue = Number(parsedHourlyRate.toFixed(2));
    const normalizedHourlyRateEffectiveDate = String(hourlyRateEffectiveDate || "").trim();
    if (!isValidEmail(normalizedEmail)) {
      showSnackbar("Please enter a valid email address", theme, "error");
      return;
    }

    const nameChanged = name.trim() !== initialName.trim();
    const emailChanged = normalizedEmail !== initialEmail.trim();
    const hourlyRateChanged =
      Number.parseFloat(initialHourlyRate || "0") !== normalizedHourlyRateValue;
    const imageChanged =
      profileImage && typeof profileImage === "object" && profileImage.full;
    const passwordChanged = currentPassword && newPassword && confirmPassword;

    if ((newPassword || confirmPassword) && !passwordChanged) {
      showSnackbar(
        "Enter current password, new password, and confirmation to update",
        theme,
        "error",
      );
      return;
    }

    if (hourlyRateChanged && !normalizedHourlyRateEffectiveDate) {
      showSnackbar("Please select an effective date for the hourly rate change", theme, "error");
      return;
    }

    if (emailChanged && !currentPassword) {
      showSnackbar(
        "Current password is required to change your email",
        theme,
        "error",
      );
      return;
    }

    if (passwordChanged && newPassword !== confirmPassword) {
      showSnackbar(
        "New password and confirmation do not match",
        theme,
        "error",
      );
      return;
    }

    if (!nameChanged && !emailChanged && !passwordChanged && !imageChanged && !hourlyRateChanged) {
      showSnackbar("No changes to save", theme, "info");
      return;
    }

    setSaving(true);

    if (emailChanged || passwordChanged) {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          showSnackbar(
            "User session expired. Please sign in again.",
            theme,
            "error",
          );
          setSaving(false);
          return;
        }
        const credential = EmailAuthProvider.credential(
          user.email,
          currentPassword,
        );
        await reauthenticateWithCredential(user, credential);
        if (passwordChanged) {
          await updatePassword(user, newPassword);
        }

        if (emailChanged) {
          await verifyBeforeUpdateEmail(user, normalizedEmail, {
            url: `${window.location.origin}/loginConfirmation`,
            handleCodeInApp: true,
          });
        }
      } catch (err) {
        console.error(err);
        const message = getFriendlyAuthError(err);
        showSnackbar(message, theme, "error");
        setSaving(false);
        return;
      }
    }

    let updatedTimesheetCount = 0;

    if (nameChanged || imageChanged || hourlyRateChanged) {
      try {
        const member = await api.get(`/members/id/${memberId}`);

        const updateData = {
          name: name,
          role: member.data.Role,
          email: normalizedEmail,
          hourlyRate: normalizedHourlyRateValue,
        };

        if (imageChanged) {
          updateData.image = profileImage.full;
          updateData.imageThumbnail = profileImage.thumbnail;
        }

        if (hourlyRateChanged) {
          updateData.hourlyRateEffectiveDate = normalizedHourlyRateEffectiveDate;
        }

        const updateResponse = await api.put(`/members/${memberId}`, updateData);
        updatedTimesheetCount = Number(updateResponse?.data?.updatedTimesheetCount || 0);

        if (setCurrentUser) {
          setCurrentUser((prev) => ({
            ...prev,
            Name: name,
            email: normalizedEmail,
            HourlyRate: normalizedHourlyRateValue,
            hourlyRate: normalizedHourlyRateValue,
            Image: imageChanged ? profileImage.full : prev?.Image,
            ImageThumbnail: imageChanged
              ? profileImage.thumbnail
              : prev?.ImageThumbnail,
          }));
        }

        // Emit socket event for real-time updates
        if (socket) {
          socket.emit("member:saved", {
            modelType: "Member",
            recordId: memberId,
            changeAction: "Updated",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }

        overlayData?.onProfileUpdated?.({
          name,
          hourlyRate: normalizedHourlyRateValue,
          image: imageChanged ? profileImage.full : overlayData?.image,
          imageThumbnail: imageChanged
            ? profileImage.thumbnail
            : overlayData?.imageThumbnail,
        });
      } catch (err) {
        console.error(err);
        handleErrorDialog(
          err.response?.data?.error || "Error updating profile",
          theme,
        );
        setSaving(false);
        return;
      }
    }

    if (emailChanged) {
      showSnackbar(
        `Verification link sent to ${normalizedEmail}. Open it to confirm the email change.`,
        theme,
        "success",
      );
      setSaving(false);
      closeOverlay();
      return;
    }

    if (hourlyRateChanged) {
      showSnackbar(
        `Changes saved. ${updatedTimesheetCount} timeslot${updatedTimesheetCount === 1 ? "" : "s"} updated with the new rate.`,
        theme,
        "success",
      );
    } else {
      showSnackbar("Changes saved", theme, "success");
    }
    setSaving(false);
    closeOverlay();
  };

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          bgcolor: "rgba(0, 0, 0, 0.8)",
          zIndex: 1300,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            bgcolor: theme.palette.background.default,
            width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
            minWidth: { xs: "100%", sm: "600px" },
            maxWidth: "800px",
            maxHeight: "80%",
            display: "flex",
            borderRadius: "1.5rem",
            justifyContent: "center",
            alignItems: "flex-start",
            flexDirection: "column",
            color: theme.palette.text.primary,
            p: "0rem .2rem 1.5rem 0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              flexDirection: "row",
              p: "0.5rem 2rem",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "start",
                alignItems: "center",
                width: "60%",
                flexDirection: "row",
                gap: 1,
              }}
            >
              <IconButton color="primary" size="large" onClick={closeOverlay}>
                <ChevronLeftRoundedIcon fontSize="inherit" />
              </IconButton>

              <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
                Edit Profile
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "end",
                alignItems: "center",
                width: "40%",
                flexDirection: "row",
                gap: 1,
              }}
            >
              {isMobile ? (
                <Tooltip title="Discard" placement="top">
                  <IconButton
                    color="primary"
                    onClick={closeOverlay}
                    sx={{
                      width: "40px",
                      height: "40px",
                      border: `2px solid ${theme.palette.primary.main}`,
                      borderRadius: "12px",
                    }}
                  >
                    <DisabledByDefaultRoundedIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  color="primary"
                  variant="outlined"
                  size="large"
                  onClick={closeOverlay}
                  sx={{
                    borderRadius: "12px",
                  }}
                >
                  Discard
                </Button>
              )}

              <Tooltip
                title={
                  isMobile && (saving || passwordsDoNotMatch)
                    ? "Please fix errors before saving"
                    : ""
                }
                placement="top"
              >
                <span>
                  {isMobile ? (
                    <IconButton
                      color="primary"
                      onClick={handleSave}
                      disabled={saving || passwordsDoNotMatch}
                      sx={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.background.default,
                        borderRadius: "12px",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                        "&.Mui-disabled": {
                          backgroundColor:
                            theme.palette.action.disabledBackground,
                          color: theme.palette.action.disabled,
                        },
                      }}
                    >
                      <SaveRoundedIcon />
                    </IconButton>
                  ) : (
                    <Button
                      loading={saving}
                      color="primary"
                      variant="contained"
                      size="large"
                      endIcon={<SaveRoundedIcon />}
                      onClick={handleSave}
                      disabled={saving || passwordsDoNotMatch}
                      sx={{
                        borderRadius: "12px",
                      }}
                    >
                      Save
                    </Button>
                  )}
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              p: "1rem 2.3rem 1rem 2.5rem",
              height: "100%",
              width: "100%",
              overflowY: "auto",
              bgcolor: theme.palette.background.alt,
              border: `1px solid ${theme.palette.primary.border2}`,
              borderRadius: "1.5rem",
              "& .MuiTextField-root": {
                m: 0,
                width: "100%",
              },
            }}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Profile
                </Typography>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: getAvatarColor(name),
                      borderRadius: "12px",
                      boxShadow: theme.shadows[3],
                    }}
                    src={profileImagePreview}
                  >
                    {name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <input
                      accept="image/*"
                      style={{ display: "none" }}
                      id="profile-image-input"
                      type="file"
                      onChange={handleImageChange}
                    />
                    <label htmlFor="profile-image-input">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<CameraAltRoundedIcon />}
                        size="small"
                        sx={{ borderRadius: "8px" }}
                      >
                        Change Photo
                      </Button>
                    </label>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      JPG, PNG, GIF up to 5MB
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Display Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={email.trim().length > 0 && !isValidEmail(email)}
                  helperText={
                    email.trim().length > 0 && !isValidEmail(email)
                      ? "Enter a valid email address"
                      : "Used for sign-in and account notifications"
                  }
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Hourly Rate"
                  type="number"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Used for monthly timesheet total calculations"
                />
              </Grid>

              {Number.parseFloat(initialHourlyRate || "0") !==
                Number.parseFloat(String(hourlyRate || "0") || "0") && (
                <Grid size={{ xs: 12 }}>
                  <DatePicker
                    fullWidth
                    size="small"
                    label="Rate Effective Date"
                    format="MM/DD/YYYY"
                    value={hourlyRateEffectiveDate ? dayjs(hourlyRateEffectiveDate) : null}
                    onChange={(newValue) => {
                      setHourlyRateEffectiveDate(
                        newValue?.isValid?.() ? newValue.format("YYYY-MM-DD") : "",
                      );
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                      },
                    }}
                    helperText="If this date is in the past, timeslots from that date until now will use the new rate."
                  />
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Password
                </Typography>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  type={showCurrentPassword ? "text" : "password"}
                  label="Current Password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowCurrentPassword(!showCurrentPassword)
                          }
                          edge="end"
                          tabIndex={-1}
                        >
                          {showCurrentPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  type={showNewPassword ? "text" : "password"}
                  label="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                          tabIndex={-1}
                        >
                          {showNewPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  type={showConfirmPassword ? "text" : "password"}
                  label="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={confirmPassword && newPassword !== confirmPassword}
                  helperText={
                    confirmPassword && newPassword !== confirmPassword
                      ? "Passwords do not match"
                      : ""
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge="end"
                          tabIndex={-1}
                        >
                          {showConfirmPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {newPassword && (
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ mt: 0.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Password Strength:
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={`${passwordStrength.color}.main`}
                      >
                        {passwordStrength.label}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={passwordStrength.strength}
                      sx={{
                        height: 6,
                        borderRadius: 1,
                        backgroundColor: theme.palette.action.hover,
                        "& .MuiLinearProgress-bar": {
                          backgroundColor:
                            theme.palette[passwordStrength.color].main,
                        },
                      }}
                    />
                    <Box sx={{ mt: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Password should contain:
                      </Typography>
                      <Typography
                        variant="caption"
                        color={
                          passwordStrength.checks?.length
                            ? "success.main"
                            : "text.secondary"
                        }
                      >
                        ✓ At least 8 characters
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {" "}
                        |{" "}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={
                          passwordStrength.checks?.uppercase
                            ? "success.main"
                            : "text.secondary"
                        }
                      >
                        ✓ Uppercase
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {" "}
                        |{" "}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={
                          passwordStrength.checks?.lowercase
                            ? "success.main"
                            : "text.secondary"
                        }
                      >
                        ✓ Lowercase
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {" "}
                        |{" "}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={
                          passwordStrength.checks?.number
                            ? "success.main"
                            : "text.secondary"
                        }
                      >
                        ✓ Number
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {" "}
                        |{" "}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={
                          passwordStrength.checks?.special
                            ? "success.main"
                            : "text.secondary"
                        }
                      >
                        ✓ Special character
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </Box>
      </Box>

      {/* Image Crop Dialog */}
      <Dialog
        open={showCropDialog}
        onClose={(event, reason) => {
          // Prevent closing on backdrop click or escape key
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.default,
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CropIcon />
            <Typography variant="h4" fontWeight="bold">
              Crop Profile Picture
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, position: "relative", height: 400 }}>
          {imageToCrop && (
            <Cropper
              image={imageToCrop}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              showGrid={false}
              style={{
                containerStyle: {
                  borderRadius: "12px",
                },
              }}
            />
          )}
        </DialogContent>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Zoom
          </Typography>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(e, zoom) => setZoom(zoom)}
            sx={{ mt: 1 }}
          />
        </Box>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={handleCropCancel}
            variant="outlined"
            sx={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCropSave}
            variant="contained"
            sx={{ borderRadius: "8px" }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SettingsProfileEdit;
